// A position is the sum of its purchases. The API does this aggregation; the
// mock server and optimistic cache updates use this same helper so the numbers
// on screen never disagree.
import type { Holding, PurchaseLot } from '@/types';

/** comparable key of when a purchase happened (a day, or the first day of the named month) */
export const lotKey = (l: PurchaseLot) => l.date ?? (l.period ? `${l.period}-01` : undefined);

/** total quantity, weighted average price, earliest known purchase */
export function aggregate(symbol: string, lots: PurchaseLot[]): Holding {
  const qty = lots.reduce((s, l) => s + l.qty, 0);
  const priced = lots.filter((l) => l.price !== undefined);
  const pricedQty = priced.reduce((s, l) => s + l.qty, 0);
  const cost = priced.reduce((s, l) => s + l.qty * l.price!, 0);
  const first = lots.filter(lotKey).sort((a, b) => lotKey(a)!.localeCompare(lotKey(b)!))[0];
  return {
    symbol, lots, qty: round(qty, 10), avgPrice: pricedQty ? round(cost / pricedQty, 8) : 0,
    cost: round(cost, 8), pricedQty: round(pricedQty, 10),
    ...(lots.some((l) => l.priceSource && l.priceSource !== 'user' && l.price !== undefined) ? { estimated: true } : {}),
    ...(priced.length < lots.length ? { incomplete: true } : {}),
    ...(first ? first.date
      ? { since: first.date, sinceSource: first.dateSource === 'history' ? 'history' as const : 'user' as const }
      : { since: first.period, sinceSource: 'period' as const } : {}),
  };
}
const round = (v: number, d: number) => Math.round(v * 10 ** d) / 10 ** d;

/** the position after one more purchase (for the «new average» preview) */
export const withLot = (h: Holding | undefined, symbol: string, lot: PurchaseLot) => aggregate(symbol, [...(h?.lots ?? []), lot]);
